import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Minus, Plus, X, Truck, Shield, ArrowLeft, Gift, CheckCircle2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Cartisempty from "../../assets/images/cart/cart-is-empty.svg";
import { useAuth } from "../../context/Auth";
import Seo from "@/seo/seo";
import { routeSeoConfig } from "@/utils/seo-config";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  loadCart,
  updateQuantity,
  removeItem,
  syncFromLocalStorage,
  validateCoupon,
  removeCoupon,
  fetchCartTotals,
  selectCartItems,
  selectCartLoading,
  selectCartSubtotal,
  selectCartTotal,
  selectCartTotals,
  selectCartTotalsLoading,
  selectCartCoupon,
  selectCartCouponLoading,
  selectCartCouponError,
} from "@/store/slices/cartSlice";
import { toast } from "sonner";
import CartSkeleton from "./cart-skeleton";
import axios from "@/utils/axios";
import { calculateShippingCost, getShippingMethodDetails, getEstimatedDelivery } from "@/utils/shippingCalculator";
import LoginModal from "@/components/auth/login/login-modal";
import SignupModal from "@/components/auth/signup/signup-modal";

export default function Cart() {
  const { user, loading: authLoading, fetchUser } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Redux selectors
  const cartItems = useAppSelector(selectCartItems);
  const loading = useAppSelector(selectCartLoading);
  const subtotal = useAppSelector(selectCartSubtotal);
  const total = useAppSelector(selectCartTotal);
  const cartTotals = useAppSelector(selectCartTotals);
  const cartTotalsLoading = useAppSelector(selectCartTotalsLoading);
  const coupon = useAppSelector(selectCartCoupon);
  const couponLoading = useAppSelector(selectCartCouponLoading);
  const couponError = useAppSelector(selectCartCouponError);
  
  // Local state for coupon code input
  const [couponCode, setCouponCode] = useState("");
  const [selectedState, setSelectedState] = useState(null);
  const [shippingCost, setShippingCost] = useState(null); // null when no state selected

  // Modal states
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  
  // Calculate discount amount from coupon
  const discountAmount = coupon?.discountAmount || 0;
  
  // Calculate shipping cost dynamically when state or cart items change
  useEffect(() => {
    if (cartItems.length > 0 && selectedState) {
      const totalWeight = cartItems.reduce((sum, item) => {
        const weight = item.weight || 0.5; // Default 0.5kg if no weight
        return sum + (weight * (item.quantity || 1));
      }, 0);
      
      const method = getShippingMethodDetails(totalWeight);
      const cost = calculateShippingCost(cartItems, selectedState, method.code);
      setShippingCost(cost);
    } else {
      // No shipping cost when no state selected
      setShippingCost(null);
    }
  }, [cartItems, selectedState]);

  // Use backend totals if available, otherwise fallback to local calculation
  const displayTax = cartTotals?.taxAmount ?? (subtotal * 0.08);

  // Ensure auth is checked on mount (in case user navigated directly here)
  useEffect(() => {
    fetchUser();
  }, []);

  // Note: No need to listen for cartUpdated events here
  // The middleware already saves to localStorage after every cart action
  // The Redux state is the source of truth, and localStorage is kept in sync automatically

  // Load cart on mount and when user changes
  useEffect(() => {
    // For guests, sync from localStorage immediately to show cart right away
    if (!user) {
      dispatch(syncFromLocalStorage());
      // Also dispatch loadCart to ensure consistency
      dispatch(loadCart(user));
    } else {
      // For authenticated users, load from server
      dispatch(loadCart(user));
    }
  }, [dispatch, user]);

  // Fetch cart totals from backend (includes tax calculated from system settings)
  // For authenticated users: call backend API
  // For guests: calculate locally via fetchCartTotals
  useEffect(() => {
    if (cartItems.length > 0) {
      const discountAmount = coupon?.discountAmount || 0;
      // Ensure shippingCost is a number (use 0 if null for guests)
      const shippingCostNum = shippingCost !== null ? Number(shippingCost) || 0 : 0;
      dispatch(fetchCartTotals({ shippingCost: shippingCostNum, discountAmount }));
    }
  }, [dispatch, user, cartItems.length, shippingCost, coupon]);

  // Handle quantity update
  const handleUpdateQuantity = (productId, change) => {
    const item = cartItems.find(item => item.productId === productId);
    if (!item) return;
    
    // Check stock before allowing increase
    const stock = item.stock || item.stock_quantity || 0;
    if (change > 0 && stock > 0 && item.quantity >= stock) {
      toast.warning(`Only ${stock} items available in stock`);
      return;
    }
    
    dispatch(updateQuantity({ productId, change, user }));
  };

  // Handle item removal
  const handleRemoveItem = (productId) => {
    dispatch(removeItem({ productId, user }));
  };

  // Handle coupon validation
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Get product IDs for eligibility check
    const productIds = cartItems
      .map(item => item.id)
      .filter(id => id && !isNaN(id));

    try {
      const result = await dispatch(validateCoupon({
        code: couponCode.trim().toUpperCase(),
        orderAmount: subtotal,
        productIds,
      })).unwrap();

      if (result) {
        toast.success("Coupon applied successfully!");
        setCouponCode(""); // Clear input after successful application
        // Refresh cart totals with discount
        // The total selector will automatically include the discount
      }
    } catch (error) {
      // Error is already handled by the thunk and stored in couponError
      // toast.error will be shown by the thunk
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    dispatch(removeCoupon());
    setCouponCode("");
    toast.success("Coupon removed");
  };

  // Show coupon error toast when error changes
  useEffect(() => {
    if (couponError) {
      toast.error(couponError);
    }
  }, [couponError]);

  const formatNaira = (amount) =>
    new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(amount);

  if (loading || authLoading) {
    return (
      <>
        <Seo {...routeSeoConfig.cart} type="website" noindex={routeSeoConfig.cart.noindex} />
        <CartSkeleton />
      </>
    );
  }

  return (
    <>
      <Seo {...routeSeoConfig.cart} type="website" noindex={routeSeoConfig.cart.noindex} />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/shop"
              className="flex items-center gap-2 text-primary hover:text-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Continue Shopping
            </Link>
            <Badge variant="secondary" className="bg-secondary text-primary">
              {cartItems.length} items
            </Badge>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {cartItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto">
            <img
              src={Cartisempty}
              alt="Cart is empty"
              className="max-w-xs w-full mb-6 drop-shadow-md"
            />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">
              Your cart is empty
            </h2>
            <p className="text-gray-500 mb-6">
              Looks like you haven't added anything to your cart yet.
            </p>
            <Button asChild className="bg-primary text-white px-6 py-2 text-sm">
              <Link to="/shop">Start Shopping</Link>
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {/* Free Shipping Banner */}
              {/* <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-secondary" />
                  <div>
                    <p className="font-medium text-primary">
                      {subtotal >= 50
                        ? "🎉 You qualify for free shipping!"
                        : `Add $${(50 - subtotal).toFixed(
                            2
                          )} more for free shipping`}
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-secondary h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min((subtotal / 50) * 100, 100)}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Cart Items List */}
              <div className="bg-white rounded-lg shadow-sm">
                {cartItems.map((item, index) => (
                  <div key={item.productId}>
                    <div className="p-6">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <div className="relative">
                          <img
                            src={item.image || "/placeholder.svg"}
                            alt={item.name}
                            width={120}
                            height={120}
                            className="rounded-lg object-cover"
                          />
                          {item.originalPrice && (
                            <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs">
                              Sale
                            </Badge>
                          )}
                        </div>

                        {/* Product Details */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-primary text-lg">
                                {item.name}
                              </h3>
                              {/* Display variation info if present */}
                              {item.variation && (
                                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                  {item.variation.variant_name && (
                                    <div className="font-medium">{item.variation.variant_name}</div>
                                  )}
                                  <div className="flex gap-2 flex-wrap">
                                    {item.variation.size && (
                                      <span>Size: {item.variation.size}</span>
                                    )}
                                    {item.variation.color && (
                                      <span>Color: {item.variation.color}</span>
                                    )}
                                    {item.variation.scent && (
                                      <span>Scent: {item.variation.scent}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              <p className="text-gray-600 text-sm mt-1">
                                {item.description}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                {/* Use finalPrice for display, basePrice for original if discount exists */}
                                {item.basePrice && item.discount > 0 ? (
                                  <>
                                    <span className="text-xl font-bold text-primary">
                                      {formatNaira(item.finalPrice || item.price)}
                                    </span>
                                    <span className="text-gray-400 line-through">
                                      {formatNaira(item.basePrice)}
                                    </span>
                                    <Badge variant="secondary" className="bg-red-500 text-white text-xs">
                                      -{item.discount}%
                                    </Badge>
                                  </>
                                ) : (
                                  <span className="text-xl font-bold text-primary">
                                    {formatNaira(item.finalPrice || item.price)}
                                  </span>
                                )}
                              </div>
                              {(item.stock !== undefined || item.stock_quantity !== undefined) && (
                                (item.stock || item.stock_quantity || 0) <= 0 ? (
                                  <Badge variant="destructive" className="mt-2">
                                    Out of Stock
                                  </Badge>
                                ) : (item.stock || item.stock_quantity || 0) < 10 ? (
                                  <Badge variant="outline" className="mt-2 text-orange-600 border-orange-600">
                                    Only {(item.stock || item.stock_quantity)} left
                                  </Badge>
                                ) : null
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-gray-400 hover:text-red-500"
                              onClick={() => handleRemoveItem(item.productId)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Quantity Controls */}
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600">
                                Quantity:
                              </span>
                              <div className="flex items-center border border-gray-200 rounded-lg">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() =>
                                    handleUpdateQuantity(item.productId, -1)
                                  }
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>

                                <span className="px-3 py-1 text-sm font-medium">
                                  {item.quantity}
                                </span>

                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() =>
                                    handleUpdateQuantity(item.productId, 1)
                                  }
                                  disabled={(item.stock || item.stock_quantity || 0) > 0 && item.quantity >= (item.stock || item.stock_quantity || 0)}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-primary">
                                {formatNaira((item.finalPrice || item.price) * item.quantity)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < cartItems.length - 1 && <Separator />}
                  </div>
                ))}
              </div>

              {/* Shipping State Selector */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-semibold text-primary mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Shipping Location
                </h3>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="shipping-state" className="text-sm text-gray-600 mb-2 block">
                      Select State for Shipping Cost
                    </label>
                    <select
                      id="shipping-state"
                      value={selectedState || ""}
                      onChange={(e) => setSelectedState(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="">Select a state...</option>
                      <option value="FCT">Federal Capital Territory (Abuja)</option>
                      <option value="Lagos">Lagos</option>
                      <option value="Kano">Kano</option>
                      <option value="Rivers">Rivers</option>
                      <option value="Kaduna">Kaduna</option>
                      <option value="Oyo">Oyo</option>
                      <option value="Ogun">Ogun</option>
                      <option value="Enugu">Enugu</option>
                      <option value="Delta">Delta</option>
                      <option value="Anambra">Anambra</option>
                      <option value="Imo">Imo</option>
                      <option value="Abia">Abia</option>
                      <option value="Edo">Edo</option>
                      <option value="Plateau">Plateau</option>
                      <option value="Niger">Niger</option>
                      <option value="Kogi">Kogi</option>
                      <option value="Kwara">Kwara</option>
                      <option value="Osun">Osun</option>
                      <option value="Ondo">Ondo</option>
                      <option value="Ekiti">Ekiti</option>
                      <option value="Cross River">Cross River</option>
                      <option value="Akwa Ibom">Akwa Ibom</option>
                      <option value="Bayelsa">Bayelsa</option>
                      <option value="Benue">Benue</option>
                      <option value="Nasarawa">Nasarawa</option>
                      <option value="Bauchi">Bauchi</option>
                      <option value="Gombe">Gombe</option>
                      <option value="Adamawa">Adamawa</option>
                      <option value="Katsina">Katsina</option>
                      <option value="Jigawa">Jigawa</option>
                      <option value="Sokoto">Sokoto</option>
                      <option value="Zamfara">Zamfara</option>
                      <option value="Kebbi">Kebbi</option>
                      <option value="Borno">Borno</option>
                      <option value="Yobe">Yobe</option>
                      <option value="Taraba">Taraba</option>
                      <option value="Ebonyi">Ebonyi</option>
                    </select>
                  </div>
                  {selectedState && (
                    <div className="text-sm text-gray-600">
                      <p>Estimated delivery: {getEstimatedDelivery(selectedState)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Promo Code */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-semibold text-primary mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Promo Code
                </h3>
                {coupon ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">{coupon.code}</p>
                          <p className="text-sm text-green-600">
                            {coupon.discountType === "percentage"
                              ? `${coupon.discountValue}% off`
                              : `₦${coupon.discountValue.toLocaleString()} off`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveCoupon}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter promo code"
                      className="flex-1"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleApplyCoupon();
                        }
                      }}
                      disabled={couponLoading}
                    />
                    <Button
                      variant="outline"
                      className="border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                    >
                      {couponLoading ? "Applying..." : "Apply"}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
                <h2 className="text-xl font-bold text-primary mb-6">
                  Order Summary
                </h2>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Subtotal ({cartItems.length} items)
                    </span>
                    <span className="font-medium">{formatNaira(subtotal)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium">
                      {shippingCost === null ? (
                        <span className="text-gray-400 text-sm italic">
                          Select state to calculate
                        </span>
                      ) : shippingCost === 0 ? (
                        <span className="text-green-600">Free</span>
                      ) : (
                        <div className="text-right">
                          <div>{formatNaira(shippingCost)}</div>
                          <div className="text-xs text-gray-500">
                            {getEstimatedDelivery(selectedState)}
                          </div>
                        </div>
                      )}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium">
                      {cartTotalsLoading ? (
                        <span className="text-gray-400">Calculating...</span>
                      ) : (
                        formatNaira(displayTax)
                      )}
                    </span>
                  </div>

                  {discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span className="font-medium">-{formatNaira(discountAmount)}</span>
                    </div>
                  )}

                  <Separator />

                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-primary">Total</span>
                    <span className="text-primary">{formatNaira(total)}</span>
                  </div>
                </div>

                {/* Login/Register Section for unauthenticated users */}
                {!user && !authLoading && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-center">
                      <h3 className="font-semibold text-blue-900 mb-2">
                        Ready to checkout?
                      </h3>
                      <p className="text-sm text-blue-700 mb-4">
                        Create an account or log in to save your cart, track orders, and checkout faster.
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Button
                          onClick={() => setLoginModalOpen(true)}
                          className="bg-primary hover:bg-primary/90 text-white px-6"
                        >
                          Log In
                        </Button>
                        <Button
                          onClick={() => setSignupModalOpen(true)}
                          variant="outline"
                          className="border-primary text-primary hover:bg-primary hover:text-white px-6"
                        >
                          Create Account
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Checkout Button - disabled if out of stock, not logged in, or auth loading */}
                <Button
                  className="w-full mt-6 bg-primary hover:bg-primary/90 text-white py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={
                    authLoading ||
                    !user ||
                    cartItems.some((item) => (item.stock || item.stock_quantity || 0) <= 0)
                  }
                  onClick={() => {
                    if (!user) {
                      toast.error("Please log in to proceed to checkout");
                      navigate("/login");
                      return;
                    }
                    navigate("/checkout");
                  }}
                >
                  {authLoading
                    ? "Checking..."
                    : !user
                    ? "Log in to checkout"
                    : cartItems.some((item) => (item.stock || item.stock_quantity || 0) <= 0)
                    ? "Remove out of stock items to proceed"
                    : "Proceed to Checkout"}
                </Button>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield className="w-4 h-4 text-secondary" />
                    <span>Secure checkout with SSL encryption</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Truck className="w-4 h-4 text-secondary" />
                    <span>Free returns within 30 days</span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-600 mb-3">We accept:</p>
                  <div className="flex gap-2">
                    {["Paystack", "Flutterwave", "Bank Transfer"].map(
                      (method) => (
                        <div
                          key={method}
                          className="bg-gray-100 rounded px-3 py-1 text-xs font-medium text-gray-600"
                        >
                          {method}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Auth Modals */}
      <LoginModal
        isOpen={loginModalOpen}
        onOpenChange={setLoginModalOpen}
        onSwitchToSignup={() => {
          setLoginModalOpen(false);
          setSignupModalOpen(true);
        }}
      />

      <SignupModal
        isOpen={signupModalOpen}
        onOpenChange={setSignupModalOpen}
        onSwitchToLogin={() => {
          setSignupModalOpen(false);
          setLoginModalOpen(true);
        }}
      />
    </>
  );
}
